/**
 * The top of the patient list box: name search, the filter bar with applied
 * chips, and the full text of long selections (approved on GDN-4090).
 *
 * Presentational. `usePatientList` owns the state; this renders it.
 */

import React, { useRef } from 'react';
import { FilterBar } from '@components/ui/FilterMenu/FilterBar';
import { describeFilter } from '@components/ui/FilterMenu/filterHelpers';
import type { FilterFieldDef, FiltersState } from '@components/ui/FilterMenu/types';
import { Clear as ClearIcon, Search as SearchIcon } from '@mui/icons-material';
import { Box, IconButton, InputAdornment, Stack, TextField, Typography } from '@mui/material';

import { selectedValues } from '../utils/patientListQuery';

export interface PatientListFilterBoxProps {
  /** Plural subject word, lowercase ("patients" / "clients"). */
  plural: string;
  fields: FilterFieldDef[];
  filters: FiltersState;
  onFiltersChange: (next: FiltersState) => void;
  search: string;
  onSearchChange: (value: string) => void;
  onClearSearch: () => void;
  onClearFilters: () => void;
  onResetView: () => void;
  resultStatement: string;
}

export const PatientListFilterBox: React.FC<PatientListFilterBoxProps> = ({
  plural,
  fields,
  filters,
  onFiltersChange,
  search,
  onSearchChange,
  onClearSearch,
  onClearFilters,
  onResetView,
  resultStatement,
}) => {
  const searchRef = useRef<HTMLInputElement>(null);
  const hasFilters = Object.keys(filters).length > 0;
  const appliedFields = fields.filter(field => filters[field.id]);

  return (
    <Stack spacing={2} sx={{ p: { xs: 1.5, sm: 2.5 } }}>
      <TextField
        fullWidth
        size="small"
        inputRef={searchRef}
        label={`Search ${plural} by name`}
        value={search}
        onChange={event => onSearchChange(event.target.value)}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  aria-label="Clear search"
                  onClick={() => {
                    onClearSearch();
                    searchRef.current?.focus();
                  }}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : undefined,
          },
        }}
      />
      {/* Long payer and requirement names wrap inside their chip instead of
          pushing the list box wider than a phone. */}
      <Box
        sx={{
          '& [aria-label="Active filters"] > .MuiStack-root': { maxWidth: '100%' },
          '& [aria-label="Active filters"] > .MuiStack-root > .MuiStack-root': {
            maxWidth: '100%',
          },
          '& [aria-label^="Edit "]': { minWidth: 0 },
          '& [aria-label^="Edit "] > .MuiTypography-root:not(:last-child)': {
            whiteSpace: 'nowrap',
            flexShrink: 0,
          },
          '& [aria-label^="Remove "]': { flexShrink: 0 },
        }}
      >
        <FilterBar
          fields={fields}
          predicates={filters}
          onPredicatesChange={onFiltersChange}
          canClearPredicates={hasFilters}
          canResetView={hasFilters || search.length > 0}
          onClearPredicates={() => {
            onClearFilters();
          }}
          onResetView={() => {
            onResetView();
            searchRef.current?.focus();
          }}
          resultStatement={resultStatement}
        />
      </Box>
      {appliedFields.length > 0 && (
        <Box component="details" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
          <Box component="summary" sx={{ cursor: 'pointer', py: 0.5 }}>
            View full selections
          </Box>
          {appliedFields.map(field => (
            <Typography key={field.id} variant="caption" sx={{ display: 'block', mt: 0.5 }}>
              {field.label}:{' '}
              {field.type === 'multiSelect'
                ? field.options
                    .filter(option => selectedValues(filters, field.id).includes(option.value))
                    .map(option => option.label)
                    .join(' or ')
                : describeFilter(field, filters[field.id]).value}
            </Typography>
          ))}
        </Box>
      )}
      <Typography variant="caption" color="text.secondary">
        Match every filter category and the name search. Within a category, match any selected
        value. Clear filters keeps your search.
      </Typography>
    </Stack>
  );
};
